// Inline-source entry builder for `<style data-luis>` blocks.
// Runtime keeps inline compilation local to the authored block without `.luis` import fetches.
import type { BuildSourceEntry } from './types'
import { resolveLuisInlineSourceText } from '../dom'
import type { LuisManifestResolution } from './manifest'
import { buildInlineSignature, buildOptionsSignature } from './signature'

// Build one inline source entry from current text content.
export function buildInlineSourceEntry(
  source: HTMLStyleElement,
  styleTag: HTMLStyleElement,
  sourceId: string,
  manifestResolution: LuisManifestResolution,
): Promise<BuildSourceEntry> {
  const rawSource = resolveLuisInlineSourceText(source)
  const optionsSignature = buildOptionsSignature(source)
  setInlineStyleSourceMode(source)
  const baseHref = resolveInlineBaseHref()

  return Promise.resolve({
    source,
    styleTag,
    sourceId,
    kind: 'inline',
    href: '',
    rawSource,
    debugSource: rawSource,
    signature: buildInlineSignature(rawSource, '', optionsSignature),
    executionOverride: manifestResolution.executionOverrideResolver?.(baseHref),
    serializedImportGraph: {},
    serializedImportModes: {},
    importedLuisHrefs: [],
    importMetrics: [],
    preCompileWarnings: [...manifestResolution.warnings],
  })
}

// Prevent browser CSS parsing for inline source blocks after processing.
function setInlineStyleSourceMode(styleTag: HTMLStyleElement): void {
  styleTag.type = 'text/luis'
}

// Resolve base href for manifest execution rules on inline style entries.
function resolveInlineBaseHref(): string {
  if (typeof document !== 'undefined' && typeof document.baseURI === 'string') {
    return document.baseURI
  }

  if (typeof window !== 'undefined' && window.location?.href) {
    return window.location.href
  }

  return '/'
}
